import React, { useEffect, useState } from 'react';
import { useVendor } from '../../hooks/useVendor';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  ShoppingCart,
  AttachMoney,
  Percent,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#d32f2f', '#f44336', '#ff9800', '#4caf50', '#2196f3'];

const Analytics = () => {
  const { analytics, loading, fetchAnalytics } = useVendor();
  const [revenueData, setRevenueData] = useState([]);
  const [orderData, setOrderData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Process API data for Charts
  useEffect(() => {
    if (analytics) {
      // 1. Process Monthly Sales for Line Chart
      if (analytics.monthlySales && Array.isArray(analytics.monthlySales) && analytics.monthlySales.length > 0) {
        // Reverse to show chronological order (Jan -> Dec)
        const sorted = [...analytics.monthlySales].reverse();
        setRevenueData(sorted.map(item => ({
          month: item.month,
          revenue: parseFloat(item.revenue || 0),
          orders: item.orders
        })));
      } else {
        // Set empty array if no data to prevent broken charts
        setRevenueData([]);
      }

      // 2. Process Order Distribution for Pie Chart
      if (analytics.orderDistribution && Array.isArray(analytics.orderDistribution) && analytics.orderDistribution.length > 0) {
        setOrderData(analytics.orderDistribution.map(item => ({
          status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
          count: item.count
        })));
      } else {
        setOrderData([]);
      }
    }
  }, [analytics]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Map Metrics from Real Backend Data (Matching the Dashboard)
  const metrics = [
    {
      title: 'Total Views',
      value: analytics?.totalViews || 0,
      icon: <Visibility />,
      color: '#2196f3',
      change: '+12%',
      trend: 'up',
    },
    {
      title: 'Total Sales',
      value: analytics?.totalSales || 0,
      icon: <ShoppingCart />,
      color: '#4caf50',
      change: '+8%',
      trend: 'up',
    },
    {
      title: 'Total Revenue',
      value: `$${analytics?.totalRevenue?.toFixed(2) || '0.00'}`,
      icon: <AttachMoney />,
      color: '#d32f2f',
      change: '+15%',
      trend: 'up',
    },
    {
      title: 'Conversion Rate',
      value: `${analytics?.conversionRate?.toFixed(2) || '0.00'}%`,
      icon: <Percent />,
      color: '#ff9800',
      change: '+3%',
      trend: 'up',
    },
  ];

  const topProducts = analytics?.topProducts || [];

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track your store's performance and growth
        </Typography>
      </Paper>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {metrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.title}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="600">
                    {metric.title}
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${metric.color}15`,
                      color: metric.color,
                    }}
                  >
                    {metric.icon}
                  </Box>
                </Stack>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                  {metric.value}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {metric.trend === 'up' ? (
                    <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                  <Typography
                    variant="caption"
                    color={metric.trend === 'up' ? 'success.main' : 'error.main'}
                    fontWeight="600"
                  >
                    {metric.change}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs last month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Revenue & Orders Overview
            </Typography>
            <Box sx={{ height: 300 }}>
              {revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#d32f2f"
                      strokeWidth={2}
                      name="Revenue ($)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      stroke="#2196f3"
                      strokeWidth={2}
                      name="Orders"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary" variant="h6">
                    No sales data available yet
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Order Distribution
            </Typography>
            <Box sx={{ height: 250 }}>
              {orderData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="count"
                    >
                      {orderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No orders available</Typography>
                </Box>
              )}
            </Box>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {orderData.map((entry, index) => (
                <Grid item xs={6} key={entry.status}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: COLORS[index % COLORS.length],
                      }}
                    />
                    <Typography variant="caption">
                      {entry.status}: {entry.count}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Top Products */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Top Products
        </Typography>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Sales</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Growth</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary">
                      No product data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                topProducts.map((product, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip
                        label={index + 1}
                        size="small"
                        color={index < 3 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {product.name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {product.salesCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600" color="primary">
                        ${(product.price * (product.salesCount || 0)).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={product.growth >= 0 ? <ArrowUpward /> : <ArrowDownward />}
                        label={`${product.growth >= 0 ? '+' : ''}${product.growth || 0}%`}
                        size="small"
                        color={product.growth >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Analytics;